/**
 * Route map page: mobile-friendly store map with SVG foreground, graph background,
 * shopping-list items, and recommendation markers restored from the previous flow.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    Fab,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    TextField,
    CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FilterListIcon from "@mui/icons-material/FilterList";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { MAIN_COLOR_RED } from "../assets/styles";
import { type GeneratedGraphData } from "../components/GeneratedGraphView";
import StoreMapGraphOverlay from "../components/StoreMapGraphOverlay";
import SelectPreferencesStep, { type ShoppingPreferences } from "../components/SelectPreferencesStep";
import BarcodeScanner from "../components/BarcodeScanner";
import FeedbackDialog from "../components/FeedbackDialog";
import { useProducts } from "../hooks/useProducts";
import { useShoppingListRedux } from "../hooks/useShoppingListRedux";
import { shoppingListApi, recommendationApi, productsApi } from "../api";
import { type SavedShoppingList } from "../redux/shoppingListSlice";
import { type RawProduct } from "../types";

import generatedGraphJson from "../assets/data/generated_graph.json";
import plantaSvg from "../assets/img/planta.svg";
import productIconRed from "../assets/img/product_icon.svg";
import productIconBlue from "../assets/img/product_icon_blue.svg";
import productIconGreen from "../assets/img/product_icon_green.svg";
import barcodeIcon from "../assets/img/barcode.svg";

const graphData = generatedGraphJson as GeneratedGraphData;
const MIN_SCALE = 0.75;
const MAX_SCALE = 10;
const SCALE_STEP = 0.5;
const STORE_ID = "1";
const INITIAL_ROUTE_SCALE = 4;
const SVG_VIEW_BOX = {
    minX: 0,
    minY: 0,
    width: 1911,
    height: 750,
};
const STORE_INNER_BOUNDS = {
    minX: 22.5,
    minY: 21,
    maxX: 1892,
    maxY: 729,
};
const CALIBRATION = {
    scale: 0.985,
    offsetX: -6,
    offsetY: 6,
};

const getPersonaIdFromStorage = (): string | null => {
    try {
        const raw = localStorage.getItem("selectedPersona");
        if (!raw) return null;
        const persona = JSON.parse(raw) as { id?: string; name?: string; created_at?: string };
        return persona.id ?? persona.name ?? persona.created_at ?? null;
    } catch {
        return null;
    }
};

const computeGraphBounds = (nodes: GeneratedGraphData["nodes"]) => {
    const entries = Object.values(nodes);
    if (!entries.length) {
        return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
    }

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const node of entries) {
        const [x = 0, y = 0] = node.coords || [];
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    }

    return { minX, minY, maxX, maxY };
};

const mapGraphPointToSvg = (
    point: { x: number; y: number },
    graphBounds: ReturnType<typeof computeGraphBounds>
) => {
    const graphWidth = Math.max(1, graphBounds.maxX - graphBounds.minX);
    const graphHeight = Math.max(1, graphBounds.maxY - graphBounds.minY);
    const normalizedX = (point.x - graphBounds.minX) / graphWidth;
    const normalizedY = (graphBounds.maxY - point.y) / graphHeight;

    const targetMinX = Math.max(SVG_VIEW_BOX.minX, STORE_INNER_BOUNDS.minX);
    const targetMinY = Math.max(SVG_VIEW_BOX.minY, STORE_INNER_BOUNDS.minY);
    const targetMaxX = Math.min(
        SVG_VIEW_BOX.minX + SVG_VIEW_BOX.width,
        STORE_INNER_BOUNDS.maxX
    );
    const targetMaxY = Math.min(
        SVG_VIEW_BOX.minY + SVG_VIEW_BOX.height,
        STORE_INNER_BOUNDS.maxY
    );
    const targetWidth = Math.max(1, targetMaxX - targetMinX);
    const targetHeight = Math.max(1, targetMaxY - targetMinY);
    const uniformScale = Math.min(targetWidth / graphWidth, targetHeight / graphHeight);
    const fittedWidth = graphWidth * uniformScale * CALIBRATION.scale;
    const fittedHeight = graphHeight * uniformScale * CALIBRATION.scale;
    const offsetX =
        targetMinX + (targetWidth - fittedWidth) / 2 + CALIBRATION.offsetX;
    const offsetY =
        targetMinY + (targetHeight - fittedHeight) / 2 + CALIBRATION.offsetY;

    return {
        x: offsetX + normalizedX * fittedWidth,
        y: offsetY + normalizedY * fittedHeight,
    };
};

const RouteMapPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { getProductById } = useProducts();
    const { addItem, removeItemFromList } = useShoppingListRedux();
    const getProductByIdRef = useRef(getProductById);
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const hasAutoFocusedRouteRef = useRef(false);
    /** When false, the next calculate-route call will send include_sdm=false (user scanned the "next" item). Cleared after use. */
    const includeSdmForNextRouteRef = useRef<boolean | null>(null);
    const scaleRef = useRef(1);
    const panRef = useRef({ x: 0, y: 0 });
    const mousePanRef = useRef<{
        isDragging: boolean;
        startX: number;
        startY: number;
        startPanX: number;
        startPanY: number;
    }>({
        isDragging: false,
        startX: 0,
        startY: 0,
        startPanX: 0,
        startPanY: 0,
    });
    const gestureRef = useRef<{
        mode: "idle" | "pan" | "pinch";
        startPanX: number;
        startPanY: number;
        startTouchX: number;
        startTouchY: number;
        startDistance: number;
        startScale: number;
        pinchCenterX: number;
        pinchCenterY: number;
    }>({
        mode: "idle",
        startPanX: 0,
        startPanY: 0,
        startTouchX: 0,
        startTouchY: 0,
        startDistance: 0,
        startScale: 1,
        pinchCenterX: 0,
        pinchCenterY: 0,
    });
    const [viewportSize, setViewportSize] = useState({ width: 1, height: 1 });
    const [scale, setScale] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [selectedList, setSelectedList] = useState<SavedShoppingList | null>(null);
    const [listOverlayOpen, setListOverlayOpen] = useState(false);
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const [legendOpen, setLegendOpen] = useState(false);
    const [scannedRandomProduct, setScannedRandomProduct] = useState<RawProduct | null>(null);
    const [preferences, setPreferences] = useState<ShoppingPreferences>({
        frozenItems: false,
        heavyItems: false,
        recommendations: false,
    });
    const [scanOpen, setScanOpen] = useState(false);
    const [scanModeOpen, setScanModeOpen] = useState(false);
    const [barcodeNotFoundOpen, setBarcodeNotFoundOpen] = useState(false);
    const [barcodeNotFoundEan, setBarcodeNotFoundEan] = useState("");
    /** Open full-screen scanner only after scan dialog has fully exited (avoids aria-hidden + focused button). */
    const openScannerAfterScanDialogExitsRef = useRef(false);
    const [manualBarcode, setManualBarcode] = useState("");
    const [shoppingListNodeIds, setShoppingListNodeIds] = useState<number[]>([]);
    const [shoppingListProductsByNode, setShoppingListProductsByNode] = useState<
        Record<number, RawProduct[]>
    >({});
    const [recommendedProductIds, setRecommendedProductIds] = useState<string[]>([]);
    const [routeRecommendedProductIds, setRouteRecommendedProductIds] = useState<string[]>([]);
    const [recommendedNodeIds, setRecommendedNodeIds] = useState<number[]>([]);
    const [routeRecommendedNodeIds, setRouteRecommendedNodeIds] = useState<number[]>([]);
    const [recommendedProductsByNode, setRecommendedProductsByNode] = useState<
        Record<number, RawProduct[]>
    >({});
    const [routeNodeIds, setRouteNodeIds] = useState<number[]>([]);
    const [isRouteLoading, setIsRouteLoading] = useState(false);
    const [shelfProductsOpen, setShelfProductsOpen] = useState(false);
    const [shelfProducts, setShelfProducts] = useState<RawProduct[]>([]);
    const [shelfRecommendedProducts, setShelfRecommendedProducts] = useState<RawProduct[]>([]);
    const [activeRecalcProductId, setActiveRecalcProductId] = useState<string | null>(null);
    const [routeRecalcStatus, setRouteRecalcStatus] = useState<"idle" | "pending" | "done">(
        "idle"
    );
    const [scannedProductIds, setScannedProductIds] = useState<string[]>([]);
    const [routeVersion, setRouteVersion] = useState(0);
    const [routeTimeMinutes, setRouteTimeMinutes] = useState<number | undefined>(undefined);
    const [currentStopIndex, setCurrentStopIndex] = useState(0);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [userMarkerNodeId, setUserMarkerNodeId] = useState<number | null>(null);
    const [userMarkerGraphPoint, setUserMarkerGraphPoint] = useState<{ x: number; y: number } | null>(
        null
    );
    const personaId = getPersonaIdFromStorage();

    const shoppingProductIds = useMemo(() => {
        if (!selectedList) return [];
        const allIds = Object.keys(selectedList.items);
        if (!scannedProductIds.length) return allIds;
        const scannedSet = new Set(scannedProductIds);
        return allIds.filter((id) => !scannedSet.has(id));
    }, [scannedProductIds, selectedList]);

    const displayItems = useMemo(
        () => (selectedList ? Object.entries(selectedList.items) : []),
        [selectedList]
    );
    const totalQuantity = useMemo(() => {
        if (!selectedList?.items) return 0;
        return Object.entries(selectedList.items).reduce((sum, [productId, qty]) => {
            return scannedProductIds.includes(productId) ? sum : sum + Number(qty || 0);
        }, 0);
    }, [scannedProductIds, selectedList]);
    const isRouteReady = Boolean(selectedList?.id) && !isRouteLoading && routeNodeIds.length > 0;
    const graphBounds = useMemo(() => computeGraphBounds(graphData.nodes), []);

    useEffect(() => {
        getProductByIdRef.current = getProductById;
    }, [getProductById]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const updateSize = () => {
            setViewportSize({
                width: Math.max(1, viewport.clientWidth),
                height: Math.max(1, viewport.clientHeight),
            });
        };

        updateSize();
        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(viewport);
        return () => resizeObserver.disconnect();
    }, []);

    const clampPan = useCallback(
        (nextPan: { x: number; y: number }, nextScale: number) => {
            if (nextScale <= 1) {
                return { x: 0, y: 0 };
            }

            const minX = viewportSize.width * (1 - nextScale);
            const minY = viewportSize.height * (1 - nextScale);

            return {
                x: Math.min(0, Math.max(minX, nextPan.x)),
                y: Math.min(0, Math.max(minY, nextPan.y)),
            };
        },
        [viewportSize.height, viewportSize.width]
    );

    const applyView = useCallback((nextScale: number, nextPan: { x: number; y: number }) => {
        scaleRef.current = nextScale;
        panRef.current = nextPan;

        if (!contentRef.current) {
            return;
        }

        contentRef.current.style.width = `${nextScale * 100}%`;
        contentRef.current.style.height = `${nextScale * 100}%`;
        contentRef.current.style.transform = `translate3d(${nextPan.x}px, ${nextPan.y}px, 0)`;
    }, []);

    useEffect(() => {
        applyView(scale, pan);
    }, [applyView, pan, scale]);

    const zoomAtPoint = useCallback(
        (nextScaleRaw: number, originX: number, originY: number) => {
            const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, nextScaleRaw));
            const currentScale = scaleRef.current;
            const currentPan = panRef.current;
            const scaleRatio = nextScale / currentScale;
            const nextPan = clampPan(
                {
                    x: originX - (originX - currentPan.x) * scaleRatio,
                    y: originY - (originY - currentPan.y) * scaleRatio,
                },
                nextScale
            );

            applyView(nextScale, nextPan);
            setScale(nextScale);
            setPan(nextPan);
        },
        [applyView, clampPan]
    );

    const focusOnNode = useCallback(
        (nodeId: number, targetScale = INITIAL_ROUTE_SCALE) => {
            const node = Object.values(graphData.nodes).find(
                (entry) => Number(entry.node_id) === nodeId
            );
            if (!node) {
                return;
            }

            const [graphX = 0, graphY = 0] = node.coords || [];
            const svgPoint = mapGraphPointToSvg({ x: graphX, y: graphY }, graphBounds);
            const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, targetScale));
            const contentWidth = viewportSize.width * nextScale;
            const contentHeight = viewportSize.height * nextScale;
            const viewBoxAspect = SVG_VIEW_BOX.width / SVG_VIEW_BOX.height;
            const contentAspect = contentWidth / Math.max(1, contentHeight);

            let renderedWidth = contentWidth;
            let renderedHeight = contentHeight;
            let offsetX = 0;
            let offsetY = 0;

            if (contentAspect > viewBoxAspect) {
                renderedWidth = contentHeight * viewBoxAspect;
                offsetX = (contentWidth - renderedWidth) / 2;
            } else {
                renderedHeight = contentWidth / viewBoxAspect;
                offsetY = (contentHeight - renderedHeight) / 2;
            }

            const pointX =
                offsetX +
                ((svgPoint.x - SVG_VIEW_BOX.minX) / SVG_VIEW_BOX.width) * renderedWidth;
            const pointY =
                offsetY +
                ((svgPoint.y - SVG_VIEW_BOX.minY) / SVG_VIEW_BOX.height) * renderedHeight;
            const nextPan = clampPan(
                {
                    x: viewportSize.width / 2 - pointX,
                    y: viewportSize.height / 2 - pointY,
                },
                nextScale
            );

            applyView(nextScale, nextPan);
            setScale(nextScale);
            setPan(nextPan);
        },
        [applyView, clampPan, graphBounds, viewportSize.height, viewportSize.width]
    );

    const resetView = useCallback(() => {
        const focusNodeId = userMarkerNodeId ?? routeNodeIds[0];
        if (focusNodeId != null) {
            focusOnNode(focusNodeId);
            return;
        }

        setScale(1);
        setPan({ x: 0, y: 0 });
    }, [focusOnNode, routeNodeIds, userMarkerNodeId]);

    const handleZoomIn = useCallback(() => {
        zoomAtPoint(
            scale + SCALE_STEP,
            viewportSize.width / 2,
            viewportSize.height / 2
        );
    }, [scale, viewportSize.width, viewportSize.height, zoomAtPoint]);

    const handleZoomOut = useCallback(() => {
        zoomAtPoint(
            scale - SCALE_STEP,
            viewportSize.width / 2,
            viewportSize.height / 2
        );
    }, [scale, viewportSize.width, viewportSize.height, zoomAtPoint]);

    const getTouchDistance = (touches: React.TouchList) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.hypot(dx, dy);
    };

    const getTouchCenter = (touches: React.TouchList) => ({
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
    });

    const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        if (event.touches.length === 2) {
            const center = getTouchCenter(event.touches);
            gestureRef.current = {
                ...gestureRef.current,
                mode: "pinch",
                startDistance: getTouchDistance(event.touches),
                startScale: scaleRef.current,
                startPanX: panRef.current.x,
                startPanY: panRef.current.y,
                pinchCenterX: center.x,
                pinchCenterY: center.y,
            };
            return;
        }

        if (event.touches.length === 1 && scaleRef.current > 1) {
            gestureRef.current = {
                ...gestureRef.current,
                mode: "pan",
                startPanX: panRef.current.x,
                startPanY: panRef.current.y,
                startTouchX: event.touches[0].clientX,
                startTouchY: event.touches[0].clientY,
            };
        }
    };

    const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
        if (gestureRef.current.mode === "pinch" && event.touches.length === 2) {
            const nextDistance = getTouchDistance(event.touches);
            const scaleFactor = nextDistance / Math.max(1, gestureRef.current.startDistance);
            const viewportRect = viewportRef.current?.getBoundingClientRect();
            const originX = gestureRef.current.pinchCenterX - (viewportRect?.left ?? 0);
            const originY = gestureRef.current.pinchCenterY - (viewportRect?.top ?? 0);

            zoomAtPoint(gestureRef.current.startScale * scaleFactor, originX, originY);
            return;
        }

        if (gestureRef.current.mode === "pan" && event.touches.length === 1) {
            const dx = event.touches[0].clientX - gestureRef.current.startTouchX;
            const dy = event.touches[0].clientY - gestureRef.current.startTouchY;
            applyView(
                scaleRef.current,
                clampPan(
                    {
                        x: gestureRef.current.startPanX + dx,
                        y: gestureRef.current.startPanY + dy,
                    },
                    scaleRef.current
                )
            );
        }
    };

    const handleTouchEnd = () => {
        if (scaleRef.current <= 1) {
            resetView();
        } else {
            setScale(scaleRef.current);
            setPan(panRef.current);
        }
        gestureRef.current.mode = "idle";
    };

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (scaleRef.current <= 1) return;
        mousePanRef.current = {
            isDragging: true,
            startX: event.clientX,
            startY: event.clientY,
            startPanX: panRef.current.x,
            startPanY: panRef.current.y,
        };
    };

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (!mousePanRef.current.isDragging) return;

            const dx = event.clientX - mousePanRef.current.startX;
            const dy = event.clientY - mousePanRef.current.startY;
            applyView(
                scaleRef.current,
                clampPan(
                    {
                        x: mousePanRef.current.startPanX + dx,
                        y: mousePanRef.current.startPanY + dy,
                    },
                    scaleRef.current
                )
            );
        };

        const handleMouseUp = () => {
            if (!mousePanRef.current.isDragging) return;
            mousePanRef.current.isDragging = false;
            setScale(scaleRef.current);
            setPan(panRef.current);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [applyView, clampPan]);

    useEffect(() => {
        if (!location.state) return;
        const state = location.state as {
            selectedList?: SavedShoppingList | null;
            preferences?: ShoppingPreferences;
            personaRecommendedProductIds?: string[];
        };

        if (state.selectedList) {
            setSelectedList(state.selectedList);
        }
        if (state.preferences) {
            setPreferences(state.preferences);
        }
        if (state.personaRecommendedProductIds?.length) {
            setRecommendedProductIds(state.personaRecommendedProductIds);
        }
    }, [location.state]);

    useEffect(() => {
        if (!preferences.recommendations || !personaId || recommendedProductIds.length > 0) {
            return;
        }

        let cancelled = false;
        recommendationApi
            .getRecommendationsByPersona(personaId)
            .then((products) => {
                if (cancelled) return;
                const ids = products
                    .map((product) => product.product_id)
                    .filter((id): id is string => Boolean(id));
                setRecommendedProductIds(ids);

                if (selectedList?.id) {
                    void shoppingListApi.auditEvent(selectedList.id, "persona-recommendations-loaded", {
                        persona_id: personaId,
                        product_ids: ids,
                        source: "route-map",
                    });
                }
            })
            .catch((error) => {
                console.warn("Failed to load persona recommendations:", error);
            });

        return () => {
            cancelled = true;
        };
    }, [personaId, preferences.recommendations, recommendedProductIds.length, selectedList?.id]);

    useEffect(() => {
        if (!selectedList?.id) {
            setRouteNodeIds([]);
            setRouteRecommendedProductIds([]);
            setRouteTimeMinutes(undefined);
            setIsRouteLoading(false);
            return;
        }

        let cancelled = false;
        setIsRouteLoading(true);
        setRouteNodeIds([]);
        setRouteRecommendedProductIds([]);
        setRouteTimeMinutes(undefined);
        const includeSdm = includeSdmForNextRouteRef.current ?? true;
        includeSdmForNextRouteRef.current = null;
        shoppingListApi
            .calculateRoute(
                selectedList.id,
                userMarkerNodeId ?? undefined,
                includeSdm
            )
            .then((result) => {
                if (cancelled) return;
                setRouteNodeIds((result.route || []).map((step) => step.nodeId));
                setRouteRecommendedProductIds(result.recommended_product_ids || []);
                setRouteTimeMinutes(result.time);
                setIsRouteLoading(false);
                if (routeRecalcStatus === "pending" && activeRecalcProductId) {
                    setRouteRecalcStatus("done");
                    setTimeout(() => {
                        setShelfProductsOpen(false);
                        setRouteRecalcStatus("idle");
                        setActiveRecalcProductId(null);
                    }, 800);
                }
            })
            .catch((error) => {
                console.warn("Failed to calculate route:", error);
                if (!cancelled) {
                    setRouteNodeIds([]);
                    setRouteRecommendedProductIds([]);
                    setRouteTimeMinutes(undefined);
                    setIsRouteLoading(false);
                    if (routeRecalcStatus === "pending" && activeRecalcProductId) {
                        setRouteRecalcStatus("done");
                        setTimeout(() => {
                            setShelfProductsOpen(false);
                            setRouteRecalcStatus("idle");
                            setActiveRecalcProductId(null);
                        }, 800);
                    }
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        activeRecalcProductId,
        routeRecalcStatus,
        routeVersion,
        selectedList?.id,
        userMarkerNodeId,
    ]);

    useEffect(() => {
        hasAutoFocusedRouteRef.current = false;
        setUserMarkerNodeId(null);
        setUserMarkerGraphPoint(null);
    }, [selectedList?.id]);

    useEffect(() => {
        if (!routeNodeIds.length || userMarkerNodeId != null || userMarkerGraphPoint) {
            return;
        }

        setUserMarkerNodeId(routeNodeIds[0]);
        setUserMarkerGraphPoint(null);
    }, [routeNodeIds, userMarkerGraphPoint, userMarkerNodeId]);

    useEffect(() => {
        if (
            isRouteLoading ||
            hasAutoFocusedRouteRef.current ||
            !routeNodeIds.length ||
            viewportSize.width <= 1 ||
            viewportSize.height <= 1
        ) {
            return;
        }

        focusOnNode(routeNodeIds[0]);
        hasAutoFocusedRouteRef.current = true;
    }, [focusOnNode, isRouteLoading, routeNodeIds, viewportSize.height, viewportSize.width]);

    useEffect(() => {
        if (!shoppingProductIds.length) {
            setShoppingListNodeIds([]);
            setShoppingListProductsByNode({});
            return;
        }

        let cancelled = false;
        Promise.allSettled(
            shoppingProductIds.map((productId) => productsApi.getProductLocation(productId, STORE_ID))
        ).then((results) => {
            if (cancelled) return;

            const nodeSet = new Set<number>();
            const byNode: Record<number, RawProduct[]> = {};

            shoppingProductIds.forEach((productId, index) => {
                const result = results[index];
                if (result.status !== "fulfilled") return;
                const nodeId = result.value?.node_id;
                if (!nodeId) return;

                nodeSet.add(nodeId);
                const product = getProductByIdRef.current(productId);
                if (!product) return;
                byNode[nodeId] = [...(byNode[nodeId] || []), product];
            });

            setShoppingListNodeIds(Array.from(nodeSet));
            setShoppingListProductsByNode(byNode);
        });

        return () => {
            cancelled = true;
        };
    }, [shoppingProductIds]);

    useEffect(() => {
        const allRecommendedIds = Array.from(
            new Set([
                ...(preferences.recommendations ? recommendedProductIds : []),
                ...routeRecommendedProductIds,
            ])
        );

        if (!allRecommendedIds.length) {
            setRecommendedNodeIds([]);
            setRouteRecommendedNodeIds([]);
            setRecommendedProductsByNode({});
            return;
        }

        let cancelled = false;
        Promise.allSettled(
            allRecommendedIds.map((productId) => productsApi.getProductLocation(productId, STORE_ID))
        ).then((results) => {
            if (cancelled) return;

            const allNodeSet = new Set<number>();
            const routeNodeSet = new Set<number>();
            const byNode: Record<number, RawProduct[]> = {};

            allRecommendedIds.forEach((productId, index) => {
                const result = results[index];
                if (result.status !== "fulfilled") return;
                const nodeId = result.value?.node_id;
                if (!nodeId) return;

                allNodeSet.add(nodeId);
                if (routeRecommendedProductIds.includes(productId)) {
                    routeNodeSet.add(nodeId);
                }

                const product = getProductByIdRef.current(productId);
                if (!product) return;
                byNode[nodeId] = [...(byNode[nodeId] || []), product];
            });

            setRecommendedNodeIds(Array.from(allNodeSet));
            setRouteRecommendedNodeIds(Array.from(routeNodeSet));
            setRecommendedProductsByNode(byNode);
        });

        return () => {
            cancelled = true;
        };
    }, [
        preferences.recommendations,
        recommendedProductIds,
        routeRecommendedProductIds,
    ]);

    const handleNodeClick = useCallback((nodeId: number) => {
        const listProductsAtNode = shoppingListProductsByNode[nodeId] || [];
        const recommendationProductsAtNode = recommendedProductsByNode[nodeId] || [];
        const listIds = new Set(listProductsAtNode.map((product) => product.unique_id));
        const uniqueRecommendations = recommendationProductsAtNode.filter(
            (product) => !listIds.has(product.unique_id)
        );

        if (!listProductsAtNode.length && !uniqueRecommendations.length) {
            return;
        }

        setShelfProducts(listProductsAtNode);
        setShelfRecommendedProducts(uniqueRecommendations);
        setShelfProductsOpen(true);
    }, [recommendedProductsByNode, shoppingListProductsByNode]);

    const handleTogglePreference = (key: keyof ShoppingPreferences) => {
        setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const routeStops = useMemo(() => {
        const scannedSet = new Set(scannedProductIds);
        return routeNodeIds
            .map((nodeId) => ({
                nodeId,
                products: (shoppingListProductsByNode[nodeId] || []).filter(
                    (product) => !scannedSet.has(product.unique_id)
                ),
            }))
            .filter((stop) => stop.products.length > 0);
    }, [routeNodeIds, scannedProductIds, shoppingListProductsByNode]);

    useEffect(() => {
        setCurrentStopIndex(0);
    }, [routeStops.length]);

    const currentStop =
        routeStops.length > 0 ? routeStops[currentStopIndex % routeStops.length] : undefined;
    const currentProduct = currentStop?.products[0];

    /**
     * Shared by camera scan (`BarcodeScanner` onScan) and manual entry (Validate in scan dialog).
     * Lookup EAN → move user marker to product location → if on list: mark scanned, SDM off when
     * scanned item is current “next” product, recalc route; if not on list: open add-to-cart dialog.
     */
    const handleBarcodeScan = useCallback(
        async (barcode: string) => {
            const trimmed = barcode.trim();
            if (!trimmed || !selectedList) return;

            setScanOpen(false);
            setScanModeOpen(false);

            let product: RawProduct;
            try {
                product = await productsApi.getProductByEan(trimmed);
            } catch (error) {
                console.warn("Product lookup by EAN failed:", error);
                setBarcodeNotFoundEan(trimmed);
                setBarcodeNotFoundOpen(true);
                return;
            }

            const productId = product.unique_id ?? product.EAN ?? "";
            if (!productId) {
                setBarcodeNotFoundEan(trimmed);
                setBarcodeNotFoundOpen(true);
                return;
            }

            try {
                const detailed = await shoppingListApi.getShoppingListItemsDetailed(selectedList.id);
                const productEntry = detailed?.products?.find((p) => p.product_id === productId);
                const itemId = productEntry?.item_ids?.[0];

                let nodeId: number | null;

                if (itemId != null) {
                    await shoppingListApi.setItemScanned(selectedList.id, itemId, true);
                    setScannedProductIds((prev) =>
                        prev.includes(productId) ? prev : [...prev, productId]
                    );

                    void shoppingListApi.auditEvent(
                        selectedList.id,
                        "scan-item-in-shopping-list",
                        {
                            product_id: productId,
                            item_id: itemId,
                            source: "route-map-scan",
                        }
                    );
                }

                const loc = await productsApi.getProductLocation(productId, STORE_ID);
                nodeId = loc?.node_id ?? null;

                if (nodeId) {
                    setUserMarkerNodeId(nodeId);
                    setUserMarkerGraphPoint(null);
                }

                if (itemId != null) {
                    const isNextItem = currentProduct && productId === currentProduct.unique_id;
                    includeSdmForNextRouteRef.current = isNextItem ? false : null;
                    setRouteVersion((prev) => prev + 1);
                } else {
                    void shoppingListApi.auditEvent(
                        selectedList.id,
                        "scan-item-not-in-shopping-list",
                        {
                            product_id: productId,
                            ean: trimmed,
                            source: "route-map-scan",
                        }
                    );
                    setScannedRandomProduct(product);
                }
            } catch (error) {
                console.error("Failed to process barcode scan:", error);
            }
        },
        [currentProduct, selectedList]
    );

    /** Same behavior as scanning with the camera — delegates to `handleBarcodeScan`. */
    const handleManualBarcodeSubmit = () => {
        const trimmed = manualBarcode.trim();
        if (!trimmed) return;
        setManualBarcode("");
        void handleBarcodeScan(trimmed);
    };

    const handleDeleteListItem = async (productId: string) => {
        if (!selectedList?.id) return;

        // Do not allow deleting the last remaining item in the list.
        if (Object.keys(selectedList.items).length <= 1) {
            return;
        }
        try {
            await shoppingListApi.deleteShoppingListItem(selectedList.id, productId);
            void shoppingListApi.auditEvent(selectedList.id, "remove-item-from-shopping-list", {
                product_id: productId,
                source: "route-map",
            });
        } catch (error) {
            console.error("Failed to delete shopping list item:", error);
        }

        removeItemFromList(productId);
        setSelectedList((prev) => {
            if (!prev) return prev;
            const { [productId]: _removed, ...restItems } = prev.items;
            return { ...prev, items: restItems };
        });

        includeSdmForNextRouteRef.current = true;
        setRouteVersion((prev) => prev + 1);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
            }}
        >
            {/* Compact header – same as previous design */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 1,
                    flexShrink: 0,
                    borderBottom: "1px solid #eee",
                }}
            >
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ color: MAIN_COLOR_RED, mr: 1 }}
                    aria-label="back"
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" fontWeight="bold">
                    My route
                </Typography>
            </Box>

            {/* Full-height map area: graph in background, planta in foreground */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    position: "relative",
                    width: "100%",
                }}
            >
                <Box
                    ref={viewportRef}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        overflow: "hidden",
                        touchAction: "none",
                        backgroundColor: "#fafafa",
                        cursor: scale > 1 ? "grab" : "default",
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <Box
                        ref={contentRef}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: `${scale * 100}%`,
                            height: `${scale * 100}%`,
                            transform: `translate3d(${pan.x}px, ${pan.y}px, 0)`,
                            transformOrigin: "0 0",
                            willChange: "transform",
                            backfaceVisibility: "hidden",
                        }}
                    >
                        <StoreMapGraphOverlay
                            graphData={graphData}
                            svgUrl={plantaSvg}
                            height="100%"
                            routeNodeIds={routeNodeIds}
                            shoppingListNodeIds={shoppingListNodeIds}
                            recommendedNodeIds={recommendedNodeIds}
                            routeRecommendedNodeIds={routeRecommendedNodeIds}
                            userMarkerNodeId={userMarkerNodeId}
                            userMarkerGraphPoint={userMarkerGraphPoint}
                            onNodeClick={handleNodeClick}
                        />
                    </Box>
                </Box>

                {isRouteLoading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 3,
                            backgroundColor: "rgba(255,255,255,0.72)",
                            backdropFilter: "blur(2px)",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1.5,
                                px: 3,
                                py: 2,
                                borderRadius: 3,
                                backgroundColor: "#fff",
                                boxShadow: 3,
                            }}
                        >
                            <CircularProgress size={28} sx={{ color: MAIN_COLOR_RED }} />
                            <Typography variant="subtitle2" fontWeight="bold">
                                Preparing your route...
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                The map will update once the route is ready.
                            </Typography>
                        </Box>
                    </Box>
                )}

                {isRouteReady && totalQuantity === 0 ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 40,
                            px: 2,
                            width: "100%",
                            maxWidth: 360,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                borderRadius: 3,
                                boxShadow: 3,
                                backgroundColor: "#fff",
                                px: 2,
                                py: 1.5,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{ color: MAIN_COLOR_RED, lineHeight: 1.1, mb: 0.5 }}
                                >
                                    Shopping is complete
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.2 }}
                                >
                                    Proceed to checkout to finalize your purchase.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ) : isRouteReady && currentProduct ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 40,
                            px: 2,
                            width: "100%",
                            maxWidth: 360,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                borderRadius: 3,
                                boxShadow: 3,
                                backgroundColor: "#fff",
                                px: 2,
                                py: 1.5,
                            }}
                        >
                            <Box sx={{ minWidth: 70 }}>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{ color: MAIN_COLOR_RED, lineHeight: 1 }}
                                >
                                    {routeTimeMinutes ?? "--"} min
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: 11 }}
                                >
                                    {totalQuantity} items
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={currentProduct.image_url}
                                    alt={currentProduct.title}
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 2,
                                        objectFit: "contain",
                                        backgroundColor: "#fafafa",
                                    }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: MAIN_COLOR_RED, fontWeight: "bold" }}
                                    >
                                        Next
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        title={currentProduct.title}
                                        sx={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            lineHeight: 1.2,
                                            maxHeight: "2.4em",
                                        }}
                                    >
                                        {currentProduct.title}
                                    </Typography>
                                </Box>
                            </Box>
                            {totalQuantity > 1 && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={async () => {
                                        if (!selectedList?.id || !currentProduct || !currentStop) {
                                            return;
                                        }

                                        // Audit the user's decision to skip this stop.
                                        void shoppingListApi.auditEvent(
                                            selectedList.id,
                                            "skip-current-stop",
                                            {
                                                product_id: currentProduct.unique_id,
                                                node_id: currentStop.nodeId,
                                                source: "route-map",
                                            }
                                        );

                                        // Remove the item from the shopping list (keeping at least one item)
                                        // and trigger a route recalculation. User position is not changed.
                                        await handleDeleteListItem(currentProduct.unique_id);
                                        setCurrentStopIndex(0);
                                    }}
                                    sx={{
                                        backgroundColor: MAIN_COLOR_RED,
                                        borderRadius: 999,
                                        px: 1.5,
                                        py: 0.5,
                                        textTransform: "none",
                                        fontSize: 12,
                                        "&:hover": { backgroundColor: "#b91c1c" },
                                    }}
                                >
                                    Skip
                                </Button>
                            )}
                        </Box>
                    </Box>
                ) : null}

                <Box
                    sx={{
                        position: "absolute",
                        left: 16,
                        bottom: 96,
                        zIndex: 4,
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "rgba(255,255,255,0.95)",
                        borderRadius: 2,
                        boxShadow: 2,
                        overflow: "hidden",
                    }}
                >
                    <IconButton size="small" onClick={handleZoomIn} aria-label="zoom in">
                        <AddIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleZoomOut} aria-label="zoom out">
                        <RemoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={resetView} aria-label="reset view">
                        <FullscreenIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* FAB bottom left – shopping list */}
                {!isRouteLoading && (
                    <Fab
                        color="primary"
                        aria-label="shopping list"
                        onClick={() => setListOverlayOpen(true)}
                        sx={{
                            position: "absolute",
                            bottom: 24,
                            left: 16,
                            backgroundColor: MAIN_COLOR_RED,
                            "&:hover": { backgroundColor: "#b91c1c" },
                        }}
                    >
                        <ShoppingCartIcon />
                    </Fab>
                )}

                {/* FAB above Filter – legend (product icons meaning) */}
                {!isRouteLoading && (
                    <Fab
                        color="primary"
                        aria-label="legend"
                        onClick={() => setLegendOpen(true)}
                        sx={{
                            position: "absolute",
                            bottom: 152,
                            right: 16,
                            backgroundColor: MAIN_COLOR_RED,
                            "&:hover": { backgroundColor: "#b91c1c" },
                        }}
                    >
                        <HelpOutlineIcon />
                    </Fab>
                )}

                {/* FAB above Scan – preferences */}
                {!isRouteLoading && (
                    <Fab
                        color="primary"
                        aria-label="preferences"
                        onClick={() => setPreferencesOpen(true)}
                        sx={{
                            position: "absolute",
                            bottom: 88,
                            right: 16,
                            backgroundColor: MAIN_COLOR_RED,
                            "&:hover": { backgroundColor: "#b91c1c" },
                        }}
                    >
                        <FilterListIcon />
                    </Fab>
                )}

                {/* Scan button bottom right */}
                <Button
                    variant="contained"
                    startIcon={
                        <Box
                            component="img"
                            src={barcodeIcon}
                            alt="Barcode"
                            sx={{ width: 24, height: 24 }}
                        />
                    }
                    onClick={() => setScanModeOpen(true)}
                    sx={{
                        position: "absolute",
                        bottom: 24,
                        right: 16,
                        backgroundColor: MAIN_COLOR_RED,
                        borderRadius: 3,
                        px: 2,
                        py: 1.5,
                        "&:hover": { backgroundColor: "#b91c1c" },
                    }}
                >
                    Scan
                </Button>

                {totalQuantity === 0 && selectedList && (
                    <Button
                        variant="contained"
                        onClick={() => setFeedbackOpen(true)}
                        sx={{
                            position: "absolute",
                            bottom: 24,
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: MAIN_COLOR_RED,
                            borderRadius: 999,
                            px: 3,
                            py: 1,
                            textTransform: "none",
                            fontWeight: "bold",
                            "&:hover": { backgroundColor: "#b91c1c" },
                        }}
                    >
                        Finish shopping
                    </Button>
                )}
            </Box>

            {/* Legend – meaning of product icons */}
            <Dialog
                open={legendOpen}
                onClose={() => setLegendOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        borderBottom: "1px solid #eee",
                    }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Map legend
                    </Typography>
                    <IconButton onClick={() => setLegendOpen(false)} size="small" aria-label="close legend">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ pt: 2, pb: 2 }}>
                    <List dense disablePadding>
                        <ListItem sx={{ alignItems: "flex-start", py: 1.5 }}>
                            <Box
                                component="img"
                                src={productIconRed}
                                alt=""
                                sx={{
                                    width: 28,
                                    height: 34,
                                    flexShrink: 0,
                                    mt: 0.25,
                                }}
                            />
                            <ListItemText
                                primary="Red pin"
                                secondary="Shopping list items"
                                primaryTypographyProps={{ fontWeight: 600 }}
                                secondaryTypographyProps={{ color: "text.secondary" }}
                                sx={{ ml: 2 }}
                            />
                        </ListItem>
                        <ListItem sx={{ alignItems: "flex-start", py: 1.5 }}>
                            <Box
                                component="img"
                                src={productIconBlue}
                                alt=""
                                sx={{
                                    width: 28,
                                    height: 34,
                                    flexShrink: 0,
                                    mt: 0.25,
                                }}
                            />
                            <ListItemText
                                primary="Blue pin"
                                secondary="Recommendations based on your shopping history"
                                primaryTypographyProps={{ fontWeight: 600 }}
                                secondaryTypographyProps={{ color: "text.secondary" }}
                                sx={{ ml: 2 }}
                            />
                        </ListItem>
                        <ListItem sx={{ alignItems: "flex-start", py: 1.5 }}>
                            <Box
                                component="img"
                                src={productIconGreen}
                                alt=""
                                sx={{
                                    width: 28,
                                    height: 34,
                                    flexShrink: 0,
                                    mt: 0.25,
                                }}
                            />
                            <ListItemText
                                primary="Green pin"
                                secondary="Recommendations based on sequential decision making process"
                                primaryTypographyProps={{ fontWeight: 600 }}
                                secondaryTypographyProps={{ color: "text.secondary" }}
                                sx={{ ml: 2 }}
                            />
                        </ListItem>
                    </List>
                </DialogContent>
            </Dialog>

            {/* Scanned random product popup – product not in list: show info + Add to cart */}
            <Dialog
                open={Boolean(scannedRandomProduct)}
                onClose={() => setScannedRandomProduct(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                {scannedRandomProduct && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 1.5,
                                borderBottom: "1px solid #eee",
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                Product found
                            </Typography>
                            <IconButton
                                onClick={() => setScannedRandomProduct(null)}
                                size="small"
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <DialogContent sx={{ pt: 2, pb: 1, display: "flex", gap: 2, alignItems: "flex-start" }}>
                            <Avatar
                                variant="square"
                                src={scannedRandomProduct.image_url}
                                alt={scannedRandomProduct.title}
                                sx={{ width: 64, height: 64, borderRadius: 1.5 }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }}>
                                    {scannedRandomProduct.title}
                                </Typography>
                                {scannedRandomProduct.brand && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {scannedRandomProduct.brand}
                                    </Typography>
                                )}
                                <DialogActions sx={{ px: 0, py: 1 }}>
                                    <Button
                                        size="small"
                                        onClick={() => setScannedRandomProduct(null)}
                                        sx={{ textTransform: "none" }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!selectedList}
                                        onClick={async () => {
                                            if (!selectedList) return;
                                            const productId = scannedRandomProduct.unique_id;
                                            try {
                                                await shoppingListApi.addShoppingListItem(
                                                    selectedList.id,
                                                    productId,
                                                    {
                                                        product_id: productId,
                                                        quantity: 1,
                                                    }
                                                );
                                                const detailed = await shoppingListApi.getShoppingListItemsDetailed(
                                                    selectedList.id
                                                );
                                                const productEntry = detailed?.products?.find(
                                                    (p) => p.product_id === productId
                                                );
                                                const itemId = productEntry?.item_ids?.[0];
                                                if (itemId != null) {
                                                    await shoppingListApi.setItemScanned(
                                                        selectedList.id,
                                                        itemId,
                                                        true
                                                    );
                                                }
                                                void shoppingListApi.auditEvent(selectedList.id, "add-item-to-shopping-list", {
                                                    product_id: productId,
                                                    source: "scan-random-product",
                                                    persona_id: personaId,
                                                });
                                                setScannedProductIds((prev) =>
                                                    prev.includes(productId) ? prev : [...prev, productId]
                                                );
                                                addItem(productId);
                                                setSelectedList((prev) =>
                                                    prev
                                                        ? {
                                                              ...prev,
                                                              items: {
                                                                  ...prev.items,
                                                                  [productId]: (prev.items[productId] || 0) + 1,
                                                              },
                                                          }
                                                        : prev
                                                );
                                                setRouteVersion((prev) => prev + 1);
                                                setScannedRandomProduct(null);
                                            } catch (error) {
                                                console.error("Failed to add product to list:", error);
                                            }
                                        }}
                                        sx={{
                                            backgroundColor: MAIN_COLOR_RED,
                                            textTransform: "none",
                                            "&:hover": { backgroundColor: "#b91c1c" },
                                        }}
                                    >
                                        Add to cart
                                    </Button>
                                </DialogActions>
                            </Box>
                        </DialogContent>
                    </>
                )}
            </Dialog>

            {/* Shopping list overlay */}
            <Dialog
                open={listOverlayOpen}
                onClose={() => setListOverlayOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2, maxHeight: "80vh" } }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        borderBottom: "1px solid #eee",
                    }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        {selectedList?.name ?? "Shopping list"}
                    </Typography>
                    <IconButton onClick={() => setListOverlayOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent dividers sx={{ p: 0 }}>
                    {displayItems.length === 0 ? (
                        <Typography color="text.secondary" sx={{ p: 2 }}>
                            No items in this list.
                        </Typography>
                    ) : (
                        <List dense>
                            {displayItems.map(([productId, qty]) => {
                                const product = getProductById(productId);
                                const name = product?.title ?? productId;
                                const isScanned = scannedProductIds.includes(productId);
                                const canDelete = displayItems.length > 1;
                                return (
                                    <ListItem
                                        key={productId}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => {
                                                    void handleDeleteListItem(productId);
                                                }}
                                                disabled={!canDelete}
                                                sx={{ color: canDelete ? "error.main" : "action.disabled" }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                variant="square"
                                                src={product?.image_url}
                                                alt={name}
                                                sx={{ width: 48, height: 48, opacity: isScanned ? 0.5 : 1 }}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={name}
                                            secondary={`Qty: ${qty}`}
                                            primaryTypographyProps={{
                                                sx: {
                                                    textDecoration: isScanned ? "line-through" : "none",
                                                    color: isScanned ? "text.secondary" : "text.primary",
                                                },
                                            }}
                                        />
                                        {isScanned && (
                                            <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 1 }} />
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={shelfProductsOpen}
                onClose={() => setShelfProductsOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2, maxHeight: "70vh" } }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        borderBottom: "1px solid #eee",
                    }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Products at this stop
                    </Typography>
                    <IconButton onClick={() => setShelfProductsOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent dividers sx={{ p: 0 }}>
                    {shelfProducts.length === 0 && shelfRecommendedProducts.length === 0 ? (
                        <Typography color="text.secondary" sx={{ p: 2 }}>
                            No products found for this location.
                        </Typography>
                    ) : (
                        <Box>
                            {shelfProducts.length > 0 && (
                                <Box sx={{ pt: 1.5, pb: shelfRecommendedProducts.length ? 0 : 1.5 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ px: 2, mb: 0.5, fontWeight: "bold" }}
                                    >
                                        In your list
                                    </Typography>
                                    <List dense>
                                        {shelfProducts.map((product) => {
                                            const isScanned = scannedProductIds.includes(product.unique_id);
                                            return (
                                                <ListItem key={`list-${product.unique_id}`}>
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            variant="square"
                                                            src={product.image_url}
                                                            alt={product.title}
                                                            sx={{ width: 48, height: 48, opacity: isScanned ? 0.5 : 1 }}
                                                        />
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={product.title}
                                                        secondary={product.brand}
                                                        primaryTypographyProps={{
                                                            sx: { textDecoration: isScanned ? "line-through" : "none", color: isScanned ? "text.secondary" : "text.primary" }
                                                        }}
                                                    />
                                                    {isScanned && (
                                                        <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 1 }} />
                                                    )}
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}

                            {shelfRecommendedProducts.length > 0 && (
                                <Box
                                    sx={{
                                        pt: 1.5,
                                        pb: 1.5,
                                        borderTop: shelfProducts.length ? "1px solid #eee" : "none",
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            px: 2,
                                            mb: 0.5,
                                            fontWeight: "bold",
                                            color: MAIN_COLOR_RED,
                                        }}
                                    >
                                        Recommendation based on your shopping history
                                    </Typography>
                                    <List dense>
                                        {shelfRecommendedProducts.map((product) => {
                                            const isPending =
                                                activeRecalcProductId === product.unique_id &&
                                                routeRecalcStatus === "pending";
                                            const isDone =
                                                activeRecalcProductId === product.unique_id &&
                                                routeRecalcStatus === "done";

                                            return (
                                                <ListItem
                                                    key={`rec-${product.unique_id}`}
                                                    secondaryAction={
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            disabled={isPending || !selectedList}
                                                            sx={{
                                                                backgroundColor: isDone
                                                                    ? "#16a34a"
                                                                    : MAIN_COLOR_RED,
                                                                color: "#fff",
                                                                textTransform: "none",
                                                                "&:hover": {
                                                                    backgroundColor: isDone
                                                                        ? "#15803d"
                                                                        : "#b91c1c",
                                                                },
                                                            }}
                                                            onClick={async () => {
                                                                if (!selectedList) return;
                                                                setActiveRecalcProductId(product.unique_id);
                                                                setRouteRecalcStatus("pending");
                                                                try {
                                                                    await shoppingListApi.addShoppingListItem(
                                                                        selectedList.id,
                                                                        product.unique_id,
                                                                        {
                                                                            product_id: product.unique_id,
                                                                            quantity: 1,
                                                                        }
                                                                    );
                                                                    const isSdmItem =
                                                                        routeRecommendedProductIds.includes(
                                                                            product.unique_id
                                                                        );
                                                                    void shoppingListApi.auditEvent(
                                                                        selectedList.id,
                                                                        isSdmItem
                                                                            ? "add-sdm-item-to-shopping-list"
                                                                            : "add-recommended-product-to-shopping-list",
                                                                        {
                                                                            product_id: product.unique_id,
                                                                            source: isSdmItem
                                                                                ? "route-sdm-recommendation"
                                                                                : "persona-recommendation",
                                                                            persona_id: personaId,
                                                                        }
                                                                    );
                                                                    addItem(product.unique_id);
                                                                    setSelectedList((prev) =>
                                                                        prev
                                                                            ? {
                                                                                  ...prev,
                                                                                  items: {
                                                                                      ...prev.items,
                                                                                      [product.unique_id]:
                                                                                          (prev.items[
                                                                                              product.unique_id
                                                                                          ] || 0) + 1,
                                                                                  },
                                                                              }
                                                                            : prev
                                                                    );
                                                                    setRouteVersion((prev) => prev + 1);
                                                                } catch (error) {
                                                                    console.error(
                                                                        "Failed to add recommended product:",
                                                                        error
                                                                    );
                                                                    setRouteRecalcStatus("idle");
                                                                    setActiveRecalcProductId(null);
                                                                } finally {
                                                                    // Route success updates the button state in the route
                                                                    // recalculation effect once the backend responds.
                                                                }
                                                            }}
                                                        >
                                                            {isPending
                                                                ? "Recalculating route..."
                                                                : isDone
                                                                  ? "Route is ready"
                                                                  : "Add to cart"}
                                                        </Button>
                                                    }
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            variant="square"
                                                            src={product.image_url}
                                                            alt={product.title}
                                                            sx={{ width: 48, height: 48 }}
                                                        />
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={product.title}
                                                        secondary={product.brand}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Preferences overlay */}
            <Dialog
                open={preferencesOpen}
                onClose={() => setPreferencesOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle>Preferences</DialogTitle>
                <DialogContent dividers>
                    <SelectPreferencesStep
                        preferences={preferences}
                        selectedList={selectedList}
                        onTogglePreference={handleTogglePreference}
                        onBack={() => setPreferencesOpen(false)}
                        onContinue={() => setPreferencesOpen(false)}
                        hideActions
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreferencesOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <FeedbackDialog
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
                onSubmit={async ({ rating, labels, comment }) => {
                    if (!selectedList) {
                        setFeedbackOpen(false);
                        navigate("/cart");
                        return;
                    }
                    try {
                        await shoppingListApi.completeShoppingList(selectedList.id, {
                            rating,
                            labels,
                            comment,
                        });
                        void shoppingListApi.auditEvent(selectedList.id, "complete-shopping-list", {
                            rating,
                            labels,
                            has_comment: Boolean(comment?.trim()),
                            source: "route-map",
                        });
                    } catch (error) {
                        console.error("Failed to submit feedback:", error);
                    } finally {
                        setFeedbackOpen(false);
                        navigate("/cart");
                    }
                }}
            />

            {scanOpen && (
                <BarcodeScanner
                    onScan={handleBarcodeScan}
                    onClose={() => setScanOpen(false)}
                />
            )}

            <Dialog
                open={barcodeNotFoundOpen}
                onClose={() => setBarcodeNotFoundOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle>Product not found</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ pt: 0.5 }}>
                        Product with barcode <strong>{barcodeNotFoundEan}</strong> is not found in our
                        database.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => setBarcodeNotFoundOpen(false)}
                        sx={{
                            backgroundColor: MAIN_COLOR_RED,
                            "&:hover": { backgroundColor: "#b91c1c" },
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Scan mode – camera or manual barcode */}
            <Dialog
                open={scanModeOpen}
                onClose={() => {
                    openScannerAfterScanDialogExitsRef.current = false;
                    setScanModeOpen(false);
                }}
                disableRestoreFocus
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
                TransitionProps={{
                    onExited: () => {
                        if (!openScannerAfterScanDialogExitsRef.current) return;
                        openScannerAfterScanDialogExitsRef.current = false;
                        requestAnimationFrame(() => {
                            setScanOpen(true);
                        });
                    },
                }}
            >
                <DialogTitle>Scan or enter barcode</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={
                                <Box
                                    component="img"
                                    src={barcodeIcon}
                                    alt="Barcode"
                                    sx={{ width: 18, height: 18 }}
                                />
                            }
                            onClick={() => {
                                openScannerAfterScanDialogExitsRef.current = true;
                                setScanModeOpen(false);
                            }}
                            sx={{
                                backgroundColor: MAIN_COLOR_RED,
                                "&:hover": { backgroundColor: "#b91c1c" },
                            }}
                        >
                            Scan with camera
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            Or enter the EAN / Barcode manually:
                        </Typography>
                        <TextField
                            label="EAN code / Barcode"
                            value={manualBarcode}
                            onChange={(event) => setManualBarcode(event.target.value)}
                            fullWidth
                            size="small"
                            autoFocus
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            openScannerAfterScanDialogExitsRef.current = false;
                            setScanModeOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleManualBarcodeSubmit}
                        variant="contained"
                        disabled={!manualBarcode.trim()}
                        sx={{
                            backgroundColor: MAIN_COLOR_RED,
                            "&:hover": { backgroundColor: "#b91c1c" },
                        }}
                    >
                        Validate
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RouteMapPage;
